import { useEffect, useState, useCallback, useRef } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const APPROVER_ROLES = new Set([
  "admin", "super_admin", "supervisor",
  "operations_manager", "chief_security_officer",
  "dispatcher",
]);

const POLL_MS = 60_000;

// Track the app's canonical title so we can restore it when the badge count
// drops to zero or the hook unmounts. First lookup wins — subsequent calls
// preserve the original even if some other component briefly mutates it.
let ORIGINAL_TITLE = null;

function updateTabTitle(count) {
  if (typeof document === "undefined") return;
  if (ORIGINAL_TITLE == null) {
    // Strip any pre-existing "(N) " prefix so an already-badged title
    // doesn't get double-badged after a hot reload.
    ORIGINAL_TITLE = document.title.replace(/^\(\d+\+?\)\s+/, "");
  }
  const label = count > 99 ? "99+" : String(count);
  document.title = count > 0 ? `(${label}) ${ORIGINAL_TITLE}` : ORIGINAL_TITLE;
}

/**
 * Poll the shared /api/approvals/pending/count endpoint so any component
 * (sidebar badge, header bell, dashboard tile) can subscribe to the same
 * cached value without hammering the API. Only approver-scope users fetch;
 * guards / clients get zero and no network traffic.
 *
 * Also mutates document.title to `(N) <app title>` so operators tabbed away
 * to Slack still see new pending reports pop up in their tab bar.
 */
export default function useApprovalsPendingCount() {
  const { user } = useAuth();
  const [total, setTotal] = useState(0);
  const active = useRef(false);

  const canFetch = APPROVER_ROLES.has((user?.role || "").toLowerCase());

  const load = useCallback(async () => {
    if (!canFetch || active.current) return;
    active.current = true;
    try {
      const res = await api.get("/approvals/pending/count");
      setTotal(Number(res.data?.total || 0));
    } catch (err) {
      // Silent-fail: badge is a hint, not a hard requirement.
      // Reset to 0 so a broken endpoint doesn't leave a stale count on screen.
      setTotal(0);
    } finally {
      active.current = false;
    }
  }, [canFetch]);

  // Sync the browser tab title with the current count.
  useEffect(() => {
    updateTabTitle(canFetch ? total : 0);
    return () => updateTabTitle(0);
  }, [total, canFetch]);

  useEffect(() => {
    if (!canFetch) { setTotal(0); return undefined; }
    load();
    const id = setInterval(load, POLL_MS);
    // Refresh whenever the tab regains focus — supervisors flip between
    // this tab and Slack often, and a stale count is worse than none.
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, [canFetch, load]);

  return { total, refresh: load };
}
